import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import CategoryFilter from "../../components/common/CategoryFilter";
import UseCaseCard from "../../components/home/UseCaseCard";
import TestimonialBanner from "../../components/home/TestimonialBanner";
import EventCard from "../../components/home/EventCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import benchmark from "../../images/home/benchmark.png";
import security from "../../images/home/security.png";
import companiesBgRm from "../../images/company.png";
import useAgentList from "../../hooks/agent/useAgentList";
import useAgentCategories from "../../hooks/agent/useAgentCategories";
import { useNavigate } from "react-router-dom";
import NavigationSidebar from "../../components/common/NavigationSidebar";
import DaiumCareBanner from "../../components/common/DaiumCareBanner";
import ScrollToTop from "../../components/common/ScrollToTop";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import {
  Activity,
  ArrowRight,
  BarChart3,
  FileText,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import AIVYBenchmarkChart from "../../components/common/AIVYBenchmarkChart";
import AIVYChatInterface from "../../components/common/AIVYChatInterface";
import AivyLifecycleAnimation from "../../components/home/AivyLifecycleAnimation";
import aivyLogo from "../../images/aivy_logo3.png";

interface StudioStats {
  surveyCount: number;
  analysisCount: number;
  participantCount: number;
  satisfactionRate: number;
}

const mapAgentKindToCategory = (agentKind: string): string => {
  switch (agentKind) {
    case "text-modal":
      return "교육";
    case "image-modal":
    case "gen-image-modal":
      return "창작 활동";
    case "emotion-modal":
      return "정서/심리";
    case "wellness-modal":
      return "웰니스";
    default:
      return "기타";
  }
};

export default function Home() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("전체");
  const [studioStats, setStudioStats] = useState<StudioStats>({
    surveyCount: 2847,
    analysisCount: 156000,
    participantCount: 523000,
    satisfactionRate: 94,
  });

  const {
    categories: apiCategories,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useAgentCategories();

  const navigationItems = [
    { label: "사용 사례", href: "/case", sectionId: "case" },
    { label: "Aivy AI Studio", href: "/studio", sectionId: "studio" },
    { label: "커뮤니티", href: "/community", sectionId: "community" },
    { label: "벤치마크", href: "/venture", sectionId: "venture" },
  ];

  const { agents, isLoading, error } = useAgentList({
    pageSize: 6,
    category: selectedCategory === "전체" ? "all" : selectedCategory,
  });

  // 카테고리와 필터링된 에이전트 계산
  const { filteredAgents, categories } = useMemo(() => {
    const baseCategories = [
      { id: "all", label: "전체" },
      ...apiCategories.map((category, index) => ({
        id: category.toLowerCase().replace(/\s+/g, "-"),
        label: category,
      })),
    ];

    if (!agents || agents.length === 0) {
      return {
        filteredAgents: [],
        categories: baseCategories,
      };
    }

    const mappedAgents = agents.map((agent) => ({
      ...agent,
      category: mapAgentKindToCategory(agent.agent_kind),
    }));

    return {
      filteredAgents: mappedAgents,
      categories: baseCategories,
    };
  }, [agents, apiCategories]);

  const handleMoreCase = () => {
    navigate("/agent-list");
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };



  return (
    <>
      <Header />
      <main className="relative w-full overflow-hidden">
        <NavigationSidebar navigationItems={navigationItems} />
        <DaiumCareBanner />
        <ScrollToTop />
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.section
            className="flex flex-col items-center gap-4 pt-20 md:pt-32"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.header
              className="text-center text-[34px] md:text-[64px] font-semibold leading-[140%] font-serif-en"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <h1 className="bg-gradient-to-r from-teal-600/80 via-green-500 to-emerald-600/50 bg-clip-text text-transparent">
                Every Moment, Cared
              </h1>
            </motion.header>
            <motion.p
              className="px-3 md:px-8 max-w-[1000px] text-gray-600 text-left sm:text-center text-base md:text-[22px] leading-[28px] whitespace-normal max-md:whitespace-pre-line font-serif-kr"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            >
              {`Aivy는 사용자의 전 생애주기를 함께하며, 아이의 성장발달을 돕는 맞춤형 케어부터 당뇨 관리, 학습 플래너까지 일상의 모든 순간을 지원하는 AI 생태계를 구축합니다. \n\n개인의 건강과 성장을 위한 체험형 솔루션부터 삶의 질을 높이는 통합 케어 서비스까지, Aivy가 당신의 더 나은 내일을 오케스트레이션합니다.`}
            </motion.p>
          </motion.section>

          <AivyLifecycleAnimation />

          <motion.section
            id="case"
            className="flex flex-col items-center mx-auto rounded-[64px] w-full gap-[40px] mb-28 md:mb-40"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center gap-[60px] max-md:gap-[24px] flex-wrap">
              <motion.header
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <motion.p
                  className="text-gray-600 text-lg md:text-2xl font-semibold leading-[24px] mb-[20px] tracking-[0.36px] font-serif-en"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  Solutions by Aivy
                </motion.p>
                <motion.h2
                  className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent font-serif-kr text-[22px] md:text-[54px] font-semibold leading-[140%] whitespace-pre-line"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  {`Aivy 서비스팀의 전문성으로\n탄생한 체험형 솔루션들을 만나보세요.`}
                </motion.h2>
                <motion.p
                  className="max-w-[1000px] px-2 md:px-0 text-base md:text-[22px] text-gray-600 leading-[25px] mt-[20px] mx-auto text-left sm:text-center font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  일상의 모든 순간을 위한 맞춤형 케어 서비스로, 당신에게 꼭
                  필요한 결과를 제공합니다.
                </motion.p>
              </motion.header>

              <motion.nav
                className="w-full px-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                {!isCategoriesLoading && categories.length > 0 && (
                  <CategoryFilter
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onCategoryChange={setSelectedCategory}
                  />
                )}
              </motion.nav>
            </div>

            <div className="w-full font-serif-kr">
              {isCategoriesLoading ? (
                <LoadingSpinner message="카테고리를 불러오는 중" size="lg" />
              ) : categoriesError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 text-lg">{categoriesError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    다시 시도
                  </button>
                </div>
              ) : isLoading ? (
                <LoadingSpinner message="사용 사례를 불러오는 중" size="lg" />
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-600 text-lg">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    다시 시도
                  </button>
                </div>
              ) : filteredAgents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredAgents.slice(0, 6).map((agent) => (
                    <UseCaseCard
                      key={agent.agent_id}
                      agent_id={agent.agent_id}
                      title={agent.agent_title}
                      image={agent.agent_image}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    선택한 카테고리에 해당하는 사용 사례가 없습니다.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleMoreCase}
                className="bg-black text-white px-6 py-3 rounded-full font-serif-kr"
              >
                더 많은 사용 사례 보기
              </button>
            </div>
          </motion.section>

          <motion.section
            className="flex flex-col items-center gap-[40px] mb-28 md:mb-40"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div id="studio" className="max-w-7xl mx-auto">
              {/* Header with integrated stats */}
              <div className="text-center mb-16">
                <motion.div
                  className="flex items-center justify-center gap-2 md:gap-4 mb-5"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <img
                    src={aivyLogo}
                    alt="Aivy Logo"
                    className="h-12 w-auto md:h-20 md:w-auto"
                  />
                  <h2 className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent text-[28px] md:text-[56px] font-bold text-gray-900 font-serif-en">
                    Aivy AI Studio
                  </h2>
                </motion.div>

                <motion.p
                  className="text-xl md:text-[26px] leading-[28px] text-gray-700 font-semibold max-w-3xl mx-auto mb-3 whitespace-pre-line md:whitespace-normal font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {`AI가 자동으로 설문을 생성하고 분석하는\n차세대 설문 플랫폼`}
                </motion.p>

                <motion.p
                  className="text-base md:text-[22px] leading-[28px] text-gray-600 max-w-3xl mx-auto mb-8 text-left sm:text-center whitespace-normal sm:whitespace-pre-line font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  {`몇 가지 정보만 입력하면 AI가 목적에 맞는 최적의 설문을 자동으로 생성하고,\n수집된 응답을 실시간으로 분석하여 인사이트를 제공합니다.`}
                </motion.p>

                {/* Inline Stats */}
                <motion.div
                  className="flex flex-wrap items-center justify-center gap-4 lg:gap-8 text-sm md:text-base font-serif-kr"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                  style={{
                    maxWidth: "100%",
                    width: "100%",
                  }}
                >
                  <div className="flex items-center justify-center gap-2 w-[calc(50%-0.5rem)] lg:w-auto">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">생성된 설문</span>
                    <span className="font-bold text-gray-900">
                      {studioStats.surveyCount}개
                    </span>
                  </div>
                  <div className="w-px h-4 bg-gray-300 hidden lg:block"></div>
                  <div className="flex items-center justify-center gap-2 w-[calc(50%-0.5rem)] lg:w-auto">
                    <BarChart3 className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">분석 완료</span>
                    <span className="font-bold text-gray-900">
                      {formatNumber(studioStats.analysisCount)}건
                    </span>
                  </div>
                  <div className="w-px h-4 bg-gray-300 hidden lg:block"></div>
                  <div className="flex items-center justify-center gap-2 w-[calc(50%-0.5rem)] lg:w-auto">
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">총 참여자</span>
                    <span className="font-bold text-gray-900">
                      {formatNumber(studioStats.participantCount)}명
                    </span>
                  </div>
                  <div className="w-px h-4 bg-gray-300 hidden lg:block"></div>
                  <div className="flex items-center justify-center gap-2 w-[calc(50%-0.5rem)] lg:w-auto">
                    <TrendingUp className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">만족도</span>
                    <span className="font-bold text-gray-900">
                      {studioStats.satisfactionRate}%
                    </span>
                  </div>
                </motion.div>
              </div>

              {/* Features Grid */}
              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 font-serif-kr"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    AI 자동 생성
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    목적과 대상만 입력하면 AI가 최적의 질문을 자동으로
                    생성합니다
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-4">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    실시간 분석
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    응답 데이터를 실시간으로 분석하여 즉각적인 인사이트를
                    제공합니다
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all border border-gray-100">
                  <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    스마트 리포트
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    AI가 분석한 결과를 시각화하고 액션 아이템을 제안합니다
                  </p>
                </div>
              </motion.div>

              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <button
                  onClick={() => {
                    window.open("https://aistudio.aivy.kr", "_blank");
                  }}
                  className="group px-8 py-4 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl flex items-center gap-3 mx-auto"
                >
                  AI Studio 바로가기
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            </div>
          </motion.section>

          <motion.section
            className="flex flex-col items-center gap-[40px] mb-28 md:mb-40 "
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center gap-[40px] self-stretch font-serif-kr">
              <motion.header
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h2 className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent text-[28px] md:text-[56px] font-bold whitespace-pre-wrap leading-[140%] font-serif-kr">
                  Aivy와 함께하는 기업들
                </h2>
              </motion.header>

              <motion.figure
                className="flex flex-col items-center relative"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="relative hover:scale-[1.01] transition-all duration-300">
                  <div className="absolute -inset-3 rounded-[44px] bg-gradient-to-br from-emerald-500/40 via-green-500/30 to-teal-500/40 shadow-lg">
                    <div className="max-w-[1100px] h-full bg-white/10 backdrop-blur-xl rounded-[41px]"></div>
                  </div>

                  <img
                    className="relative w-full max-w-[1000px] bg-gray-50 max-h-[400px] object-cover select-none rounded-[32px] z-10"
                    src={companiesBgRm}
                    alt="Aivy와 함께하는 기업들"
                  />
                </div>

                <motion.figcaption
                  className="max-w-4xl text-[#A6A6A6] text-center font-normal text-base whitespace-pre-wrap  mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <span className="mb-2 w-full text-base md:text-lg">
                    {`디에이블 (Aivy 플랫폼 운영기업) 협업 및 지원 파트너사`}
                  </span>
                </motion.figcaption>
              </motion.figure>
            </div>
          </motion.section>

          <motion.section
            id="community"
            className="flex flex-col items-center gap-[40px] mb-28 md:mb-40"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center gap-[40px] self-stretch">
              <motion.header
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <motion.p
                  className="text-gray-600 text-xl md:text-2xl font-semibold leading-[24px] mb-[20px] tracking-[0.36px] font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  사용자 리뷰
                </motion.p>
                <motion.h2
                  className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent text-[28px] md:text-[54px] font-semibold leading-[140%] font-serif-kr"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Aivy AI Studio를 직접 경험한 전문가들의 이야기
                </motion.h2>
                <motion.p
                  className="mt-5 text-base md:text-[22px] max-w-[1200px] text-gray-600 text-left sm:text-center font-medium leading-[27px] sm:whitespace-pre-wrap whitespace-normal font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {`교육, 의료, 기업 등 다양한 분야에서\nAivy AI Studio로 혁신을 만들어가는 교수님들과 기업 관계자분들의 생생한 후기를 확인해보세요.`}
                </motion.p>
              </motion.header>
              <motion.div
                className="w-full"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <TestimonialBanner className="mt-12" />
              </motion.div>
            </div>
          </motion.section>

          {/* <motion.section
            className="flex flex-col items-center gap-[40px] mb-28 md:mb-40"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center gap-[40px] self-stretch flex-wrap">
              <motion.header
                className="flex flex-col items-center gap-5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <motion.h2
                  className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent  max-md:text-[24px] font-serif-kr text-center text-5xl font-semibold leading-[140%] px-8 md:px-0"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  오프라인 이벤트
                </motion.h2>
                <motion.p
                  className="px-8 md:px-0 max-md:leading-[18px] max-md:text-[14px] max-w-[1000px] text-gray-600 text-center text-[length:20px] font-medium leading-[25px] whitespace-pre-wrap"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  {`Aivy를 직접 만나보세요. 전국 주요 행사장에서 Aivy의 체험형 솔루션을 직접 경험하실 수 있습니다. 현장에서만 느낄 수 있는 생생한 AI 케어 서비스를 만나보세요.`}
                </motion.p>
              </motion.header>
              <motion.div
                className="w-full"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <EventCard
                    image={benchmark}
                    title="Aivy 개발자 컨퍼런스 2024"
                    date="2024년 3월 15일"
                  />
                  <EventCard
                    image={security}
                    title="AI 보안 워크숍"
                    date="2024년 4월 20일"
                  />
                  <EventCard
                    image={benchmark}
                    title="Aivy 사용자 밋업"
                    date="2024년 5월 10일"
                  />
                </div>
              </motion.div>

              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <button className="bg-black text-white px-6 py-3 rounded-full">
                  더 많은 이벤트 탐색
                </button>
              </motion.div>
            </div>
          </motion.section> */}

          <motion.section
            id="venture"
            className="flex flex-col items-center gap-[40px] mb-28 md:mb-40"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center self-stretch">
              <motion.h2
                className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent text-[28px] md:text-[56px] font-semibold leading-[140%] font-serif-kr mb-[20px] text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                벤치마크
              </motion.h2>
              <motion.p
                className="text-base md:text-[22px] leading-[28px] max-w-[1000px] mb-[40px] text-gray-600 text-left sm:text-center font-medium md:whitespace-pre-wrap whitespace-normal font-serif-kr"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {`Aivy는 학습·추론·코드 생성 등 전반적인 역량이 상당히 높기 때문에,\n일반적인 비즈니스용 애플리케이션에서 해결 능력에 대한 밴치마크입니다.`}
              </motion.p>
              <motion.div
                className="w-full"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <AIVYBenchmarkChart />
              </motion.div>
              <motion.figure
                className="mt-[32px] flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <figcaption className=" text-[#A6A6A6] text-center font-normal text-base md:text-lg whitespace-pre-wrap font-serif-kr">
                  Aivy는 구글에서 개발된 오픈소스 LLM인 Gemma-3 모델을 기반으로
                  미세조정된 모델입니다.
                </figcaption>
              </motion.figure>
            </div>
          </motion.section>

          <motion.section
            className="flex flex-col items-center gap-[40px]"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div
              className="flex justify-center space-x-2 opacity-30"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.3 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            </motion.div>
            <div className="flex flex-col justify-center items-center self-stretch">
              <motion.h2
                className="bg-gradient-to-r from-teal-500 via-green-500 to-emerald-500 bg-clip-text text-transparent text-[28px] md:text-[56px] font-semibold leading-[140%] font-serif-kr mb-[20px] text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                보안성
              </motion.h2>
              <motion.p
                className=" mb-[40px] text-base md:text-[22px] leading-[28px] max-w-[1000px] text-gray-600 text-left sm:text-center font-medium whitespace-normal sm:whitespace-pre-line font-serif-kr"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {`Aivy는 사용자의 데이터를 외부로 유출하지 않습니다.\n사용자의 데이터는 사용자 고유 소유이며 철저한 보안 프로토콜을 통해 보안됩니다.`}
              </motion.p>
              <motion.figure
                className="flex flex-col items-center gap-[20px]"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col justify-center items-center h-full relative rounded-[18px] overflow-hidden">
                  <AIVYChatInterface />
                </div>
                <motion.figcaption
                  className="max-w-4xl text-[#A6A6A6] text-center font-normal text-base md:text-[17px] pb-32 whitespace-pre-wrap font-serif-kr"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  <p className="mb-2 w-full text-left sm:text-center">
                    {`Aivy는 Gemma기반으로 MoE 아키텍처를 플랫폼 측면으로 구현하였으며, 이에 따라 MCP 프로토콜이 요구됩니다`}
                  </p>
                  <p className="text-left sm:text-center">
                    {`MCP 프로토콜에서 사용자 데이터가 외부로 요청될 수 있으며 Aivy는 MCP 프로토콜에서 사용자의 소중한 데이터를 마스킹 처리하고 요청할 Expert만을 분석하여 고객의 데이터 유출을 방어합니다.`}
                  </p>
                </motion.figcaption>
              </motion.figure>
            </div>
          </motion.section>
        </div>
      </main>
      <Footer />
    </>
  );
}
